import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation32() {
  const { lesson, course, accent } = findLesson("piano-foundation", "32");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>"Hand independence" = each hand doing different things. <strong>Finger independence</strong> = each <em>finger</em> doing its own thing within one hand.</Highlight>
        <Highlight>The drill: hold one finger pressed down on a five-finger position (C-D-E-F-G) and play the other four notes around it, freely.</Highlight>
        <Highlight>Cycle through all five fingers — thumb, index, middle, <strong>ring</strong>, pinky. The ring finger is the hardest because of its natural linkage to the middle and pinky.</Highlight>
        <Highlight>Improvise melodies inside the four-finger limitation. The held finger is "sleeping" — its note is gone from the available palette.</Highlight>
        <Highlight>Take regular breaks. Each hand gets 5-6 minutes max. While the right hand rests, switch the same drill to the left.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Two kinds of independence" title="Hands · then fingers" accent={accent.hex}>
        <p>
          Almost everything earlier in this course has been about <strong>hand independence</strong>{" "}
          — one hand doing a melody while the other holds chords; one playing a riff while
          the other supports a pulse. This lesson goes one level deeper to{" "}
          <strong>finger independence</strong>: training each finger inside a single hand
          to act on its own.
        </p>

        <Vocab>
          <Term name="Hand independence" def="each hand doing a different musical task simultaneously." />
          <Term name="Finger independence" def="each finger acting independently from the others within the same hand. The harder skill." />
          <Term name="Held tone" def="a note that's pressed and sustained while the other fingers continue moving around it." />
        </Vocab>

        <KeyIdea>
          We play the piano with our fingers. Hand independence gets you a long way; finger
          independence gets you the rest of the way.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The setup" title="Five-finger position · C-D-E-F-G" accent={accent.hex}>
        <p>
          Park the right hand on the same C major five-finger position from Lesson 5: thumb
          on C, index on D, middle on E, ring on F, pinky on G. The drill is going to use
          this hand position throughout — but ask one finger at a time to <em>stay down</em>{" "}
          while the others keep playing.
        </p>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "1" },
            { pitch: "D4", color: "#22c55e", label: "2" },
            { pitch: "E4", color: "#22c55e", label: "3" },
            { pitch: "F4", color: "#22c55e", label: "4" },
            { pitch: "G4", color: "#22c55e", label: "5" },
          ]}
          height={170}
        />
      </Insight>

      <Insight eyebrow="The drill" title="Hold one finger · play the other four" accent={accent.hex}>
        <Steps>
          <Step>
            <strong>Thumb held down on C.</strong> Play the other four — D, E, F, G — in any
            order. The C keeps ringing underneath. Notice how the rhythm subtly turns into a
            triplet feel because one of the five "slots" is now permanent.
          </Step>
          <Step>
            <strong>Index held down on D.</strong> The D is now "sleeping" — gone from the
            available notes. Play C, E, F, G freely above and below the held D. This is harder.
          </Step>
          <Step>
            <strong>Middle held down on E.</strong> Even harder. The middle finger is the
            longest; holding it forces the others into less natural shapes.
          </Step>
          <Step>
            <strong>Ring held down on F.</strong> The hardest of the five. The ring is
            biologically linked to the middle and pinky — keeping it down while the others
            move feels like fighting your own anatomy.
          </Step>
          <Step>
            <strong>Pinky held down on G.</strong> Surprisingly, this one feels relatively
            free again — the pinky is at the edge of the hand and doesn't compete with the
            others.
          </Step>
        </Steps>

        <Pull>
          Maybe you could kind of push yourself and see how far you can go to kind of
          keep this down at all times while playing the others.
        </Pull>
      </Insight>

      <Insight eyebrow="Improvise inside the limit" title="Make a melody from the four free notes" accent={accent.hex}>
        <p>
          Once you can hold the chosen finger steady, don't just play the other four in
          mechanical order — <em>improvise</em> a small melody from them. Pick a song you
          half-know and try to play it with one finger artificially absent.
        </p>

        <p>
          For example, with the index held on D: try to fool around with a melody that
          uses only C, E, F, G. The natural temptation to play D will be everywhere — the
          drill is the discipline of <em>not</em> giving in.
        </p>

        <Tip>
          If your keyboard has a dual layer, you can dual the C-major position with a
          string patch — that way the held note will sustain audibly the whole time, like a
          drone underneath your melody.
        </Tip>
      </Insight>

      <Insight eyebrow="Variations" title="Change the third · change the fourth" accent={accent.hex}>
        <p>
          Once the basic drill feels boring, change the five-finger position. Make the third
          a <strong>minor third</strong> (E → E♭). Or the fourth a <strong>sharp 4</strong>{" "}
          (F → F♯ — Tivra Ma). Or the second a <strong>minor second</strong> (D → D♭). The
          drill stays the same; only the harmonic colour shifts.
        </p>

        <p>
          With each variation, your held finger is now anchoring a different chord quality.
          The exercise sharpens the ear at the same time as the fingers.
        </p>
      </Insight>

      <Insight eyebrow="Take breaks" title="5-6 minutes per hand · then swap" accent={accent.hex}>
        <Pull>
          You don't have to take a break by like going to the balcony and chilling out.
          If you want, you can work out your left hand.
        </Pull>

        <p>
          Right hand for 5 minutes → left hand for 5 minutes → right hand again. Both hands
          need the drill, and switching gives each one recovery time. Strain in the wrist or
          forearm is a clear stop signal — pause, shake out, return at half tempo.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-33"
        label="Lesson 33 · Basic Finger Independence Drills (Part 2)"
        why="Part 1 holds one finger and lets the others wander. Part 2 adds a five-note pattern that lifts only one finger at a time while the others all stay pressed — even harder."
      />
    </LessonShell>
  );
}
